import { React } from 'libs/react';
import { Icon } from 'components/Icon';
import { Money } from 'components/Money';
import { Text } from 'components/Text';
import { useDealTaxBreakdown } from 'modules/deal/hooks/useDealTaxBreakdown';
import './DealTaxBreakdownSection.scss';
/**
 * @function getTypeLabel
 * @since 1.0.0
 * @hidden
 */
function getTypeLabel(type) {
    switch (type) {
        case 'package':
            return t('Package');
        case 'option':
            return t('Option');
        default:
            return t('Item');
    }
}
/**
 * @function getApplicableTaxesLabel
 * @since 1.0.0
 * @hidden
 */
function getApplicableTaxesLabel(line) {
    if (!line.taxable) {
        return t('Not taxable');
    }
    if (line.additionalTaxes.length === 0) {
        return t('Regional taxes');
    }
    return line.additionalTaxes
        .map((tax) => `${tax.title} (${tax.rate}%)`)
        .join(', ');
}
/**
 * @component DealTaxBreakdownSection
 * @since 1.0.0
 *
 * Lists every item, package and option of the deal along with its taxable
 * status and the taxes that apply to it, so users configuring the deal can
 * quickly understand what is contributing to the tax total shown in the
 * budget summary.
 */
export function DealTaxBreakdownSection(props) {
    const { deal } = props;
    const { lines, appliedTaxes, additionalTaxesTotal, taxes } = useDealTaxBreakdown(deal);
    return (React.createElement("div", { className: "deal-tax-breakdown-section" },
        React.createElement(Text, { className: "deal-tax-breakdown-section-title", value: t('Taxes') }),
        React.createElement("table", { className: "deal-tax-breakdown-section-table" },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", null, t('Line')),
                    React.createElement("th", null, t('Type')),
                    React.createElement("th", { className: "column-taxable" }, t('Taxable')),
                    React.createElement("th", null, t('Applicable taxes')),
                    React.createElement("th", { className: "column-amount" }, t('Taxable amount')))),
            React.createElement("tbody", null, lines.map((line) => (React.createElement("tr", { key: line.id },
                React.createElement("td", { "data-label": t('Line') },
                    React.createElement(Text, { className: "deal-tax-breakdown-section-table-value", value: line.name })),
                React.createElement("td", { "data-label": t('Type') },
                    React.createElement(Text, { className: "deal-tax-breakdown-section-table-value", value: getTypeLabel(line.type) })),
                React.createElement("td", { "data-label": t('Taxable'), className: "column-taxable" },
                    React.createElement(Icon, { className: line.taxable
                            ? 'deal-tax-breakdown-section-table-taxable-icon deal-tax-breakdown-section-table-taxable-icon--yes'
                            : 'deal-tax-breakdown-section-table-taxable-icon deal-tax-breakdown-section-table-taxable-icon--no', value: line.taxable ? 'check' : 'xmark', tooltip: line.taxable ? t('Taxable') : t('Not taxable') })),
                React.createElement("td", { "data-label": t('Applicable taxes') },
                    React.createElement(Text, { className: "deal-tax-breakdown-section-table-value deal-tax-breakdown-section-table-value--muted", value: getApplicableTaxesLabel(line) })),
                React.createElement("td", { "data-label": t('Taxable amount'), className: "column-amount" },
                    React.createElement(Money, { value: line.taxable ? line.lineAmount : 0 }))))))),
        React.createElement("div", { className: "deal-tax-breakdown-section-footer" },
            appliedTaxes.map((tax) => (React.createElement("div", { key: tax.id, className: "deal-tax-breakdown-section-footer-row" },
                React.createElement(Text, { value: `${tax.title} (${tax.rate}%)` }),
                React.createElement(Money, { value: tax.amount })))),
            additionalTaxesTotal > 0 && (React.createElement("div", { className: "deal-tax-breakdown-section-footer-row" },
                React.createElement(Text, { value: t('Item specific taxes') }),
                React.createElement(Money, { value: additionalTaxesTotal }))),
            React.createElement("div", { className: "deal-tax-breakdown-section-footer-row deal-tax-breakdown-section-footer-row--total" },
                React.createElement(Text, { value: t('Total taxes') }),
                React.createElement(Money, { value: taxes })))));
}
